/**
 * CA upload form: choose a certificate file (PEM/DER/PFX/P7B) or paste PEM,
 * with an optional PFX password. The server parses and merges (never
 * persists); the merged bundle goes back through `onMerged`. The password
 * only ever travels in the request body and is cleared after each attempt.
 */

import { useRef, useState, type ReactElement } from 'react';
import { adminApi, type RuntimeConfigCtx } from '../../api/adminApi';
import {
  describeCaUploadError,
  readFileBase64,
  summarizeCaUpload,
  utf8ToBase64,
} from '../../utils/caBundle';

interface CaUploadFormProps {
  featureId: string;
  name: string;
  ctx: RuntimeConfigCtx | undefined;
  existing: string;
  editable: boolean;
  onRequestEdit: () => void;
  onMerged: (bundle: string) => Promise<void>;
  disabled: boolean;
}

interface Outcome {
  summary: string;
  skipped: string[];
}

export function CaUploadForm({
  featureId, name, ctx, existing, editable, onRequestEdit, onMerged, disabled,
}: CaUploadFormProps): ReactElement {
  const [file, setFile] = useState<File | null>(null);
  const [pem, setPem] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const handleAdd = async (): Promise<void> => {
    if (!editable) { onRequestEdit(); return; }
    setBusy(true);
    setError(null);
    setOutcome(null);
    const pw = password;
    setPassword('');
    try {
      const fileB64 = file ? await readFileBase64(file) : utf8ToBase64(pem);
      const r = await adminApi.parseCaUpload(featureId, name, ctx, {
        fileB64, password: file && pw ? pw : null, existing,
      });
      if (r.added > 0) await onMerged(r.bundle);
      setOutcome({ summary: summarizeCaUpload(r), skipped: [...r.skipped_not_ca, ...r.skipped_expired] });
      setFile(null);
      setPem('');
      if (fileInput.current) fileInput.current.value = '';
    } catch (err) {
      setError(describeCaUploadError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="runtime-config__ca-upload">
      <div className="runtime-config__ca-inputs">
        <label>
          Certificate file
          <input
            ref={fileInput}
            type="file"
            aria-label="Certificate file"
            accept=".pem,.crt,.cer,.der,.pfx,.p12,.p7b,.p7c"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); }}
          />
        </label>
        <label>
          PFX password
          <input
            type="password"
            aria-label="PFX password"
            autoComplete="off"
            value={password}
            onChange={(e) => { setPassword(e.target.value); }}
          />
        </label>
      </div>
      <textarea
        className="runtime-config__textarea"
        rows={4}
        aria-label="Paste PEM certificates"
        placeholder="…or paste PEM certificates here"
        value={pem}
        onChange={(e) => { setPem(e.target.value); }}
      />
      <button
        type="button"
        className="dialog__btn dialog__btn--primary"
        disabled={(!file && !pem.trim()) || busy || disabled}
        onClick={() => { void handleAdd(); }}
      >
        {busy ? 'Adding…' : 'Add certificates'}
      </button>
      {error && <div className="dialog__error" role="alert">{error}</div>}
      {outcome && (
        <div className="runtime-config__ca-summary" role="status">
          <p>{outcome.summary}</p>
          {outcome.skipped.length > 0 && <p>Skipped: {outcome.skipped.join('; ')}</p>}
        </div>
      )}
    </div>
  );
}
